import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, SelectChangeEvent } from '@mui/material';
import {
  ListPageScaffold,
  ListPageMain,
  ListPageFiltersSection,
} from '../components/common/list-page';
import CommonPagination from '../components/common/Pagination';
import EmptyStateMessage from '../components/common/EmptyStateMessage';
import SellerManagementFilters from '../components/sales/seller-management/SellerManagementFilters';
import SellerManagementTable from '../components/sales/seller-management/SellerManagementTable';
import SellerDetailDrawer from '../components/sales/seller-management/SellerDetailDrawer';
import {
  emptySellerManagementFilters,
  type SellerManagementFiltersState,
  type SellerManagementRow,
} from '../components/sales/seller-management/types';
import { sellersService, type SellerListFilterParams } from '../services/sellers.service';
import { LocationsService } from '../api';

const filtersToParams = (
  filters: SellerManagementFiltersState,
  page: number,
  limit: number,
): SellerListFilterParams => {
  const params: SellerListFilterParams = { page, limit };
  if (filters.searchTerm.trim()) params.searchTerm = filters.searchTerm.trim();
  if (filters.locationId !== '') params.locationId = Number(filters.locationId);
  if (filters.saleDateFrom) params.saleDateFrom = filters.saleDateFrom;
  if (filters.saleDateTo) params.saleDateTo = filters.saleDateTo;
  return params;
};

const SellerManagementPage: React.FC = () => {
  const [draftFilters, setDraftFilters] = useState(emptySellerManagementFilters);
  const [appliedFilters, setAppliedFilters] = useState(emptySellerManagementFilters);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [rows, setRows] = useState<SellerManagementRow[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [locations, setLocations] = useState<Array<{ id: number; name: string }>>([]);
  const [pagination, setPagination] = useState({ currentPage: 1, itemsPerPage: 10 });
  const [detailSellerId, setDetailSellerId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await sellersService.list(
        filtersToParams(appliedFilters, pagination.currentPage, pagination.itemsPerPage),
      );
      setRows(Array.isArray(res.data) ? res.data : []);
      setTotalItems(Number(res.total ?? 0));
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Error al cargar los vendedores';
      setError(msg);
      setRows([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, pagination.currentPage, pagination.itemsPerPage]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  useEffect(() => {
    const loadLocations = async () => {
      try {
        const res = await LocationsService.locationsControllerFindAll();
        const list = Array.isArray(res) ? res : ((res as { data?: unknown[] })?.data ?? []);
        setLocations(
          (list as Array<{ id: number; name: string }>).map((l) => ({
            id: l.id,
            name: l.name,
          })),
        );
      } catch {
        setLocations([]);
      }
    };
    void loadLocations();
  }, []);

  const handleSearch = () => {
    if (
      draftFilters.saleDateFrom !== '' &&
      draftFilters.saleDateTo !== '' &&
      draftFilters.saleDateFrom > draftFilters.saleDateTo
    ) {
      setError('La fecha "desde" no puede ser posterior a "hasta".');
      return;
    }
    setError(null);
    setAppliedFilters(draftFilters);
    setPagination((p) => ({ ...p, currentPage: 1 }));
  };

  const handleReset = () => {
    const empty = emptySellerManagementFilters();
    setDraftFilters(empty);
    setAppliedFilters(empty);
    setPagination((p) => ({ ...p, currentPage: 1 }));
  };

  const handleClearAppliedField = useCallback((field: keyof SellerManagementFiltersState) => {
    setDraftFilters((prev) => ({ ...prev, [field]: '' }));
    setAppliedFilters((prev) => ({ ...prev, [field]: '' }));
    setPagination((p) => ({ ...p, currentPage: 1 }));
  }, []);

  const handleViewSeller = (id: string) => {
    setDetailSellerId(id);
    setDetailOpen(true);
  };

  const handlePageChange = (page: number) => {
    setPagination((p) => ({ ...p, currentPage: page }));
  };

  const handleItemsPerPageChange = (event: SelectChangeEvent<unknown>) => {
    setPagination({ currentPage: 1, itemsPerPage: Number(event.target.value) });
  };

  return (
    <ListPageScaffold>
      <ListPageMain sectionGap={1}>
        <ListPageFiltersSection>
          <SellerManagementFilters
            filters={draftFilters}
            setFilters={setDraftFilters}
            appliedFilters={appliedFilters}
            onClearAppliedField={handleClearAppliedField}
            showMoreFilters={showMoreFilters}
            onToggleMoreFilters={() => setShowMoreFilters((v) => !v)}
            onSearch={handleSearch}
            onReset={handleReset}
            locations={locations}
            isLoading={loading}
          />
        </ListPageFiltersSection>

        {error && (
          <Box sx={{ py: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && !error && rows.length === 0 && (
          <EmptyStateMessage message="No hay vendedores con estos filtros." />
        )}

        {!loading && !error && rows.length > 0 && (
          <>
            <SellerManagementTable sellers={rows} onViewSeller={handleViewSeller} />
            <Box sx={{ mt: 3 }}>
              <CommonPagination
                currentPage={pagination.currentPage}
                itemsPerPage={pagination.itemsPerPage}
                totalItems={totalItems}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                itemsPerPageOptions={[10, 25, 50, 100]}
              />
            </Box>
          </>
        )}
      </ListPageMain>

      <SellerDetailDrawer
        open={detailOpen}
        sellerId={detailSellerId}
        onClose={() => {
          setDetailOpen(false);
          setDetailSellerId(null);
        }}
      />
    </ListPageScaffold>
  );
};

export default SellerManagementPage;
